// Pure predicate for Sentry `beforeSend`: identifies browser/runtime errors that
// are known to be benign or not caused by our application code, so they can be
// dropped instead of paging us. Kept free of any Sentry types so it is trivially
// unit-testable; Root.tsx computes these primitives from the Sentry event and
// delegates here.

export type KnownBenignBrowserErrorInput = {
  // Concatenated error text (type + value + message + rejection reason).
  // NOTE: Root.tsx joins the exception `type` and `value` with a SPACE, not a
  // colon ("NotFoundError The object can not be found here."), so patterns
  // matching across that boundary must treat the colon as optional.
  message: string;
  // Route the event occurred on, if known.
  pathname: string | null;
  // True for in-app webviews / email link-preview renderers (Apple Mail, WKWebView,
  // iOS embedded webviews).
  isEmbeddedBrowser: boolean;
  // True for Safari / WebKit-family runtimes.
  isSafariLike: boolean;
  // True when at least one stack frame points at a real first-party source file
  // (not node_modules/vendor, not "[native code]"/document URLs, not sourceless).
  hasRealApplicationSourceFrame: boolean;
  // True when there are no frames, or every frame lacks a usable source location.
  hasOnlySourcelessFrames: boolean;
  // The `function` name of each stack frame (for matching engine-internal frames).
  frameFunctions: string[];
  // The source `filename` of each stack frame (for matching third-party libs).
  frameFilenames: string[];
};

const EXTENSION_OBJECT_NOT_FOUND =
  /Object Not Found Matching Id:\d+,\s*MethodName:\w+,\s*ParamCount:\d+/i;

export const isKnownBenignBrowserError = ({
  message,
  pathname,
  isEmbeddedBrowser,
  isSafariLike,
  hasRealApplicationSourceFrame,
  hasOnlySourcelessFrames,
  frameFunctions,
  frameFilenames,
}: KnownBenignBrowserErrorInput): boolean => {
  // ORA-FE-4S: a browser extension's content script injects this rejection
  // ("Object Not Found Matching Id:1, MethodName:update, ParamCount:4"). It is
  // never our code, on any route.
  if (EXTENSION_OBJECT_NOT_FOUND.test(message)) {
    return true;
  }

  // ORA-FE-RF: WebKit's internal media track-menu builder, triggered when an
  // email client (e.g. Apple Mail) renders a link preview of a page with media.
  // Surfaces as a bare "Type error" from a native frame.
  if (frameFunctions.includes('sortedTrackListForMenu')) {
    return true;
  }

  // ORA-264: do NOT add a rule here for WebKit's "NotFoundError: The object can
  // not be found here." That is Safari's wording for the commit-phase DOM
  // failure Blink reports as "Failed to execute 'removeChild' on 'Node'"
  // (ORA-FE-R1) — a crash that bricks the page, not noise. We dropped it until
  // ORA-262 established what it does: React aborts the commit half-done and
  // leaves a painted but inert subtree, recoverable only by reloading the
  // document. `isUnrecoverableDomCommitError` matches this exact wording and
  // reloads on it, so filtering it here would mean silently hiding the one
  // error we consider bad enough to tear the page down for. The stack is always
  // React-internal, which is why "no first-party source frame" looked like a
  // safe gate and was in practice always true. Sentry ORA-FE-SH is a live
  // example: the recovery fired, and this filter ate the evidence.

  // ORA-FE-67 / ORA-FE-RE: benign <video> media interruptions on the video
  // route — playback aborted by navigation/teardown/source-swap, or a WebKit
  // media element transiently in an invalid state. The player already handles
  // play failures gracefully; these escape as unhandled rejections we cannot
  // catch at the call site.
  if (pathname === '/study/video') {
    if (
      /AbortError:?\s*The operation was aborted/i.test(message) &&
      !hasRealApplicationSourceFrame
    ) {
      return true;
    }

    if (/InvalidStateError/i.test(message) && isSafariLike) {
      return true;
    }
  }

  // ORA-FE-RA: the web-vitals telemetry lib (pulled in transitively by PostHog
  // performance autocapture) uses APIs like Array.prototype.at that ancient
  // in-app webviews lack, throwing "this.o.at is not a function". It is pure
  // measurement code — any error originating in it is non-actionable for app
  // correctness, so drop anything thrown from a web-vitals frame.
  if (frameFilenames.some((filename) => /web-vitals/i.test(filename))) {
    return true;
  }

  // ORA-FE-QV: stack overflow inside an embedded in-app browser's own polyfills
  // on the auth screen, with no usable (sourceless) stack to action.
  if (
    /Maximum call stack size exceeded/i.test(message) &&
    pathname === '/auth' &&
    isEmbeddedBrowser &&
    hasOnlySourcelessFrames
  ) {
    return true;
  }

  // ORA-FE-RM: an email client / in-app webview (e.g. Apple Mail) renders a link
  // preview of a content page and a WebKit API is transiently in an invalid
  // state ("InvalidStateError: The object is in an invalid state."). Only ever
  // seen from embedded / link-preview renderers — a signed-in user is never in
  // one, so a real user can't hit this (the video-route sibling above covers the
  // one genuine in-app Safari case). Gated on isEmbeddedBrowser, so ordinary
  // Safari users are unaffected.
  if (
    /InvalidStateError:?\s*The object is in an invalid state/i.test(message) &&
    isEmbeddedBrowser
  ) {
    return true;
  }

  // ORA-FE-TG: three.js r185's `onContextRestore` crashes when WebKit fires
  // `webglcontextrestored` on a renderer whose `initGLContext` already threw
  // (the context was lost mid-construction, so `info` was never assigned and
  // the handler reads `info.autoReset` off undefined). Only seen from Apple
  // Mail's link-preview renderer loading /auth: the WebGL error boundary had
  // already replaced the canvas, so nothing depends on the dead renderer. The
  // listener fires from the DOM, outside React, so no boundary can catch it.
  // Client-side the frames are the minified three chunk
  // ("https://app.oraai.com/three.module.<hash>.js"), not `onContextRestore`,
  // so we key on the chunk filename plus the `autoReset` property — which only
  // exists on three's WebGLInfo.
  if (
    /TypeError[\s\S]*\bautoReset\b/.test(message) &&
    frameFilenames.some((filename) => /three\.module/i.test(filename))
  ) {
    return true;
  }

  return false;
};
